import { useTranslation } from 'react-i18next';

import { formatDateForBackend } from '@/utils/format-date-for-backend';
import { useNavigate } from '@tanstack/react-router';

import { useGetRoomsByPropertyId } from '@/features/accommodation/hooks/use-get-rooms-by-property-id';
import { useAccommodationStore } from '@/features/accommodation-details/store/accommodation-store';
import { useBookingFilterStore } from '@/features/filters/store';

import { Loader } from '@/components/loader/loader';
import { PageHeader } from '@/components/page-header/page-header';
import { Typography } from '@/components/typography/typography';

import { MainButton } from '@/lib/shadcn/components/ui/main-button/main-button';
import { Stack } from '@/lib/shadcn/components/ui/stack/stack';

import { ErrorPageLayout } from './error-page-layout';
import { ConflictIcon } from './icons/conflict-icon';

export function ConflictErrorPage() {
  const { t } = useTranslation();
  const { formData } = useAccommodationStore();
  const bookingType = useBookingFilterStore((s) => s.selectedType);
  const filters = useBookingFilterStore((s) => s.filters[s.selectedType]);
  const navigate = useNavigate();

  const {
    data: rooms,
    isPending,
    isError,
    error,
  } = useGetRoomsByPropertyId({
    propertyId: formData.accommodationId,
    roomTypeId: formData.apartmentId || undefined,
    arrivalDate: formatDateForBackend(filters.dates.from),
    departureDate: formatDateForBackend(filters.dates.to),
    adults: filters.guests.adults,
    childAges: filters.guests.childrenAges,
  });

  if (isPending) {
    return <Loader />;
  }

  if (isError) {
    throw error;
  }

  const handleClick = () => {
    if (rooms.length === 0) {
      navigate({
        to: '/available-accommodations',
        replace: true,
        search: {
          bookingType,
          regionId: filters.region.id,
          regionName: filters.region.name,
          cityId: filters.city.id,
          cityName: filters.city.name,
          timeZone: filters.city.timeZone,
          arrivalDate: formatDateForBackend(filters.dates.from),
          departureDate: formatDateForBackend(filters.dates.to),
          adults: filters.guests.adults,
          childAges: filters.guests.childrenAges,
        },
      });
    } else {
      navigate({
        to: '/accommodation-details',
        replace: true,
        search: {
          bookingType,
          regionId: filters.region.id,
          regionName: filters.region.name,
          cityId: filters.city.id,
          cityName: filters.city.name,
          timeZone: filters.city.timeZone,
          arrivalDate: formatDateForBackend(filters.dates.from),
          departureDate: formatDateForBackend(filters.dates.to),
          adults: filters.guests.adults,
          childAges: filters.guests.childrenAges,
          accommodationId: formData.accommodationId,
          apartmentId: formData.apartmentId,
          propertyType: formData.propertyType,
        },
      });
    }
  };

  return (
    <ErrorPageLayout
      headerComponent={<PageHeader right className="w-full justify-between" />}
    >
      <Stack gap={14} align="center" className="mt-[16.5vh] flex flex-1">
        <ConflictIcon />
        <Stack gap={15} align="center">
          <Stack gap={6} className="text-center">
            <Typography.h3 className="text-teal-500">
              {t('errors.conflict.title')}
            </Typography.h3>
            <Typography.p3 className="text-slate-400">
              {t('errors.conflict.description')}
            </Typography.p3>
          </Stack>
          <MainButton className="w-full" onClick={handleClick}>
            {t('errors.conflict.button')}
          </MainButton>
        </Stack>
      </Stack>
    </ErrorPageLayout>
  );
}
